<script lang="ts" setup>
import { i18n } from '@/i18n';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { ref, watch, onMounted, computed } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import {
  FilterContentItem,
  FilterModalType,
  FreeColumnFilterContentItem,
  isDateRangeFilterType,
  isFreeColumnFilterType,
} from './filterModal';
import { useFilterModalStore } from './useFilterModalStore';
import { storeToRefs } from 'pinia';
import { StyleValue } from 'vue';
import MultipleSelectInput from '../SelectInput/MultipleSelectInput.vue';
import DateTimeRangeFilter from './DateTimeRangeFilter.vue';
import FreeColumnFilter from './FreeColumnFilter.vue';
import { LottieAnimation } from 'lottie-web-vue';
import loadingAnimation from '@/assets/loadingAnimation.json';
import { FilterType } from '@/coreDomainsModule/entities/filter';

const store = useFilterModalStore();
const { state } = storeToRefs(store);

// props
const props = withDefaults(
  defineProps<{
    filterModalType: FilterModalType;
    selectInputIndex?: number; // SelectInputのindexは被ってはいけないため、開始数を親コンポーネントから渡す
    filterItems?: FilterContentItem[];
    freeColumnFilterItems?: FreeColumnFilterContentItem[];
  }>(),
  {
    selectInputIndex: 0,
    filterItems: undefined,
    freeColumnFilterItems: undefined,
  }
);

// emits
const emits = defineEmits<{
  (e: 'apply', filterContent: FilterContentItem[], freeColumnFilterItems: FreeColumnFilterContentItem[]): void;
}>();

onMounted(() => {
  store.$reset();
  store.setFilterItems(props.filterModalType);
  store.updateFilterItems(props.filterItems);
  store.updateFreeColumnFilterItems(props.freeColumnFilterItems);
});

// watch
watch(
  () => props.filterItems,
  (newValue) => {
    store.updateFilterItems(newValue);
  }
);

watch(
  () => props.freeColumnFilterItems,
  (newValue) => {
    store.updateFreeColumnFilterItems(newValue);
  }
);

watch(
  () => props.filterModalType,
  (newValue) => {
    store.setFilterItems(newValue);
    store.updateFilterItems(props.filterItems);
    store.updateFreeColumnFilterItems(props.freeColumnFilterItems);
  }
);

const filterModalContentClass = computed(() => {
  switch (props.filterModalType) {
    case 'customerForecast':
      return 'filter-modal-content--forecast';
    case 'order':
      return 'filter-modal-content--order';
    default:
      return '';
  }
});

const filterModalStyle = computed((): StyleValue => {
  return {
    top: `${coordinateFilterButton.value.y - 16}px`,
    left: `${coordinateFilterButton.value.x - 16}px`,
    maxHeight: `${800 - coordinateFilterButton.value.y - 16}px`,
  };
});

/**
 * フィルター情報の選択数をカウントする
 * @returns 選択数
 */
const filterItemsCount = computed((): number => {
  let count = 0;
  for (const item of state.value.filterItems) {
    if (item.filterType === 'LAST_CHECKED_AT') {
      const hasRange = item.selectedValue[0] !== undefined || item.selectedValue[1] !== undefined;
      const includeNull = item.includeNull ?? false;
      if (hasRange || includeNull) {
        count = count + 1;
      }
      continue;
    }
    if (isDateRangeFilterType(item.filterType)) {
      const hasRange = item.selectedValue.length > 0 && (item.selectedValue[0] || item.selectedValue[1]);
      const includeNull = item.includeNull ?? false;
      if (hasRange || includeNull) {
        count = count + 1;
      }
      continue;
    }
    if (item.selectedValue.length > 0) {
      count = count + item.selectedValue.length;
    }
  }
  for (const item of state.value.freeColumnFilterItems) {
    count = count + item.selectedValue.length;
  }
  return count;
});

//変数
const t = i18n.t;

const coordinateFilterButton = ref({
  x: 0,
  y: 0,
});
const modalState = ref(false);
const isWaitingAggFreeColLoaded = ref(false);
const isWaitingBaseFreeColLoaded = ref(false);
const isDisplayLoading = computed(() => isWaitingAggFreeColLoaded.value || isWaitingBaseFreeColLoaded.value);
/**
 * フィルターボタンクリック時の処理
 */
const onClickFilterButton = () => {
  const buttonContent = document.getElementsByClassName('select-filter-button')[0];
  if (!(buttonContent instanceof HTMLElement)) return;
  isWaitingAggFreeColLoaded.value = state.value.filterItems.some((item) => item.filterType === 'AGGREGATE_FREE_COLUMN');
  isWaitingBaseFreeColLoaded.value = state.value.filterItems.some((item) => item.filterType === 'BASE_FREE_COLUMN');
  coordinateFilterButton.value = { x: buttonContent.offsetLeft, y: buttonContent.offsetTop };
  modalState.value = true;
};

/**
 * 適用ボタンクリック時の処理
 */
const onClickApply = () => {
  modalState.value = false;
  emits('apply', store.appliedFilterContent, store.appliedFreeColumnFilterContent);
};

/**
 * 閉じるボタンクリック時/モーダル外クリック時の処理
 */
const onClickClose = () => {
  modalState.value = false;
};

/**
 * 全設定クリアボタンクリック時の処理
 */
const onClickClear = () => {
  store.resetAllSetting();
  modalState.value = false;
  emits('apply', store.appliedFilterContent, store.appliedFreeColumnFilterContent);
};

/**
 * ロード終了時処理
 * @param filterType - 当該フィルタタイプ
 */
const onLoaded = (filterType: FilterType) => {
  if (isWaitingAggFreeColLoaded.value && filterType === 'AGGREGATE_FREE_COLUMN') {
    isWaitingAggFreeColLoaded.value = false;
  } else if (isWaitingBaseFreeColLoaded.value && filterType === 'BASE_FREE_COLUMN') {
    isWaitingBaseFreeColLoaded.value = false;
  }
};
</script>

<template>
  <button id="select-filter-button" class="select-filter-button" @click="onClickFilterButton">
    <div class="select-filter-button__container">
      <p class="select-filter-button__text">{{ filterItemsCount > 0 ? t('フィルター中') : t('フィルター') }}</p>
      <SvgLoader class="select-filter-button__icon" name="arrowDownBlue" />
      <div v-if="filterItemsCount > 0" class="speech-bubble">
        <div class="circle">
          {{ filterItemsCount }}
        </div>
      </div>
    </div>
  </button>
  <Teleport v-if="modalState" to="body">
    <div class="filter-modal-bg" @click="onClickClose" />
    <div class="filter-modal-container" :style="filterModalStyle">
      <div class="filter-modal-container__main-content main-content">
        <div v-if="isDisplayLoading" class="main-content__content-container content-container">
          <LottieAnimation :animation-data="loadingAnimation" :auto-play="true" :loop="true" :speed="1" />
        </div>
        <div class="main-content__content-container content-container" :style="{ display: isDisplayLoading ? 'none' : 'inherit' }">
          <div class="filter-modal-content" :class="filterModalContentClass">
            <div class="filter-modal-content__header header">
              <button class="close-button" @click="onClickClose">
                <div class="close-button__container">
                  <p class="close-button__text">{{ t('forecastForReceipt_closeDetailFilter') }}</p>
                  <SvgLoader class="close-button__icon" name="arrowUpBlue" />
                </div>
              </button>
              <button class="header__reset-all-button" @click="onClickClear">
                {{ t('forecastForReceipt_clearAllForDetailFilter') }}
              </button>
            </div>

            <div class="filter-modal-content__main-content main-content">
              <span class="main-content__title">{{ t('forecastForReceipt_filterByItem') }}</span>
              <div class="main-content__filter-item-container">
                <template v-for="(item, index) in state.filterItems" :key="item.filterType">
                  <div class="main-content__filter-item filter-item">
                    <DateTimeRangeFilter
                      v-if="item.filterType === 'LAST_CHECKED_AT'"
                      :model-value="state.filterItems[index].selectedValue"
                      :include-null="state.filterItems[index].includeNull ?? false"
                      :label="item.i18nOptions ? t(item.label, item.i18nOptions) : t(item.label)"
                      @update:model-value="(value) => (state.filterItems[index].selectedValue = value)"
                      @update:include-null="(value) => (state.filterItems[index].includeNull = value)"
                    />
                    <DateTimeRangeFilter
                      v-else-if="isDateRangeFilterType(item.filterType)"
                      :model-value="state.filterItems[index].selectedValue"
                      :include-null="state.filterItems[index].includeNull ?? false"
                      :label="item.i18nOptions ? t(item.label, item.i18nOptions) : t(item.label)"
                      hide-time
                      @update:model-value="(value) => (state.filterItems[index].selectedValue = value)"
                      @update:include-null="(value) => (state.filterItems[index].includeNull = value)"
                    />
                    <FreeColumnFilter
                      v-else-if="isFreeColumnFilterType(item.filterType)"
                      :index="selectInputIndex + index"
                      :model-value="state.freeColumnFilterItems.filter((freeColumnItem) => freeColumnItem.filterType === item.filterType)"
                      :filter-type="item.filterType"
                      :filter-modal-type="filterModalType"
                      :is-open-prop="state.currentFilterType === item.filterType"
                      @click:select-input="() => (state.currentFilterType = item.filterType)"
                      @loaded="() => onLoaded(item.filterType)"
                      @update:model-value="(e) => store.updateFreeColumnFilterItemsByFilterType(item.filterType, e)"
                    />
                    <MultipleSelectInput
                      v-else
                      class="filter-item__select-input"
                      :index="selectInputIndex + index"
                      :selected-list="item.selectedValue"
                      :placeholder="t('選択してください')"
                      :label="item.i18nOptions ? t(item.label, item.i18nOptions) : t(item.label)"
                      :filter-type="item.filterType"
                      :is-open-prop="state.currentFilterType === item.filterType"
                      :filter-modal-type="filterModalType"
                      use-teleport
                      @click:select-input="() => (state.currentFilterType = item.filterType)"
                      @update:model-value="(e) => (state.filterItems[index].selectedValue = e)"
                    />
                  </div>
                </template>
              </div>
            </div>

            <div class="filter-modal-content__footer footer">
              <TextButton
                :button-style="'btn-primary'"
                :size="'btn-md'"
                :on-click="onClickApply"
                class="footer__button footer__button--confirm"
              >
                {{ t('適用') }}
              </TextButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.filter-modal-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9;
}

.select-filter-button {
  width: auto;
  height: 32px;
  padding: 0 12px;
  background-color: none;
  display: grid;
  place-content: center;
  line-height: 24px;

  &__container {
    display: grid;
    grid-template-columns: auto 24px auto;
    place-items: center;
    gap: 4px;
  }

  &__text {
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: $semanticPrimary;
  }
}

.filter-modal-container {
  position: absolute;
  width: max-content;
  max-width: 73%;
  z-index: 11;
  padding: 16px;
  border-radius: 8px;
  background-color: $semanticWhite;
  box-shadow: 0 0 22px 0 #80808066;
  overflow: scroll;
}

.filter-modal-content {
  height: 100%;
  display: grid;
  gap: 16px;

  &--forecast {
    grid-template-rows: repeat(2, auto) 1fr auto;
  }
  &--order {
    grid-template-rows: auto 1fr auto;
  }

  &__header {
    display: grid;
    grid-template-columns: auto auto;
    justify-content: space-between;
    align-items: center;
  }

  &__unset-option {
    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 4px;
    justify-content: start;
  }

  &__main-content {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    gap: 8px;
  }

  &__footer {
    display: grid;
    place-items: center;
  }
}

.close-button {
  height: 32px;
  padding: 0 12px;
  background-color: none;
  display: grid;
  place-content: center;
  line-height: 24px;

  &__container {
    display: grid;
    grid-template-columns: auto 24px;
    place-items: center;
    gap: 4px;
  }

  &__text {
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
    color: $semanticPrimary;
  }
}

.header {
  &__reset-all-button {
    height: max-content;
    font-size: 16px;
    line-height: 16px;
    font-weight: 400;
    color: $semanticPrimary;
    border-bottom: 1px solid $semanticPrimary;
  }
}

.unset-option {
  &__checkbox {
    width: 20px;
    height: 20px;
  }

  &__text {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    color: $textDefault;
  }
}

.main-content {
  &__title {
    font-size: 14px;
    line-height: 14px;
    font-weight: 400;
    color: $textDefault;
  }

  &__filter-item-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: flex-start;
  }

  &__filter-item {
    display: grid;
    grid-template-rows: repeat(2, auto);
  }
}

.filter-item {
  &__title {
    font-size: 14px;
    line-height: 34px;
    font-weight: 400;
    color: $textDefault;
  }

  &__select-input {
    width: 160px;
  }
}

.footer {
  display: grid;
  place-items: center;

  &__button {
    width: 160px;
    height: 40px;
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    padding: 6px 56px 6px 56px;
  }
}

.speech-bubble {
  position: relative;
  display: inline-block;
  left: -8px;
  top: -10px;
}

.circle {
  width: 16px;
  height: 16px;
  background-color: $semanticPrimary;
  color: $semanticWhite;
  border-radius: 50%;
  text-align: center;
  line-height: 16px;
  font-size: 12px;
  font-weight: bold;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);
  position: relative;
}
</style>
